import React, { useCallback, useMemo, useState } from 'react';
import CopySVG from '~/credsystem/assets/svgs/copy.svg';
import CheckSVG from '~/credsystem/assets/svgs/check-white.svg';
import ShareSVG from '~/credsystem/assets/svgs/share.svg';
import Modal from 'react-native-modal';
import useToast from '~/hooks/useToast';
import { formatMoney } from '~/utils';
import { formatDueDate, getMonthName } from '~/utils/date';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import CloseSVG from '~/assets/svgs/close.svg';
import usecredsystemDataStore from '~/credsystem/stores/credsystemData';
import MenuOption from './components/MenuOption';
import PendingInvoiceAlert from './components/PendingInvoiceAlert';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';

export type PayInvoiceModalProps = {
  visible: boolean;
  invoiceData?: {
    invoiceValue?: number;
    dueDate?: string;
    invoiceId?: string;
    status?: InvoiceStatus;
  };
  onClose: () => void;
};

export const PayInvoiceModal: React.FC<PayInvoiceModalProps> = ({
  onClose,
  visible,
  invoiceData,
}) => {
  const { showError } = useToast();

  const [barcode, setBarcode] = useState<string | null>('');
  const [loadingCopyBarcode, setLoadingCopyBarcode] = useState(false);
  const [copiedBarcode, setCopiedBarcode] = useState(false);
  const [loadingShareBarcode, setLoadingShareBarcode] = useState(false);
  const [sharedBarcode, setSharedBarcode] = useState(false);

  const { invoiceValue, invoiceMonth, invoiceDueDate, invoiceStatus } =
    useMemo(() => {
      if (invoiceData) {
        const dueDate = formatDueDate(invoiceData.dueDate);
        const dueDay = dueDate.split('/')[0];
        return {
          invoiceValue:
            (invoiceData.invoiceValue &&
              formatMoney(invoiceData.invoiceValue)) ??
            'Indisponível',
          invoiceMonth: invoiceData.dueDate
            ? getMonthName(invoiceData.dueDate)
            : 'Indisponível',
          invoiceDueDate: dueDay ? parseInt(dueDay, 10) : 'Indefinido',
          invoiceStatus: invoiceData.status || 'Indisponível',
        };
      }

      return {
        invoiceValue: 'Indisponível',
        invoiceMonth: 'Indisponível',
        invoiceDueDate: 'Indisponível',
        invoiceStatus: 'Indisponível',
      };
    }, [invoiceData]);

  const handleOnClose = useCallback(() => {
    setTimeout(() => {
      setBarcode('');
      setCopiedBarcode(false);
      setSharedBarcode(false);
    }, 500);
    onClose();
  }, [onClose]);

  // @handlers
  const handleShareBarcode = useCallback(async () => {
    if (barcode) {
      if (MarisaAppUtils.shareItem) await MarisaAppUtils.shareItem(barcode);
      setSharedBarcode(true);
    }

    setLoadingShareBarcode(true);

    try {
      const barcodeResponse = await usecredsystemDataStore
        .getState()
        .getBarCode(invoiceData?.invoiceId);

      if (barcodeResponse) {
        if (MarisaAppUtils.shareItem)
          await MarisaAppUtils.shareItem(barcodeResponse);
        setBarcode(barcodeResponse);
        setSharedBarcode(true);
      } else {
        showError({
          title: 'A fatura não possui código de barras',
        });
      }
    } catch (error) {
      showError({
        title: 'Ocorreu um erro ao copiar o código de barras.',
      });
    } finally {
      setLoadingShareBarcode(false);
    }
  }, [barcode, invoiceData?.invoiceId, showError]);

  const handleCopyBarcode = useCallback(async () => {
    if (barcode) {
      if (MarisaAppUtils.copyToClipboard)
        MarisaAppUtils.copyToClipboard(barcode);
      setCopiedBarcode(true);
    }

    setLoadingCopyBarcode(true);

    try {
      const barcodeResponse = await usecredsystemDataStore
        .getState()
        .getBarCode(invoiceData?.invoiceId);

      if (barcodeResponse) {
        if (MarisaAppUtils.copyToClipboard)
          MarisaAppUtils.copyToClipboard(barcodeResponse);
        setBarcode(barcodeResponse);
        setCopiedBarcode(true);
      } else {
        showError({
          title: 'A fatura não possui código de barras',
        });
      }
    } catch (error) {
      showError({
        title: 'Ocorreu um erro ao compartilhar o código de barras.',
      });
    } finally {
      setLoadingCopyBarcode(false);
    }
  }, [barcode, invoiceData?.invoiceId, showError]);

  return (
    <Modal
      isVisible={visible}
      onBackdropPress={handleOnClose}
      onSwipeComplete={handleOnClose}
      onBackButtonPress={handleOnClose}
      swipeDirection={['down']}
      propagateSwipe
    >
      <View className="flex bg-gray-100 rounded-lg p-6 items-center w-[80%] self-center">
        <TouchableOpacity
          className="absolute right-4 top-4"
          onPress={handleOnClose}
        >
          <CloseSVG height={24} width={24} color={'#C5C0C0'} />
        </TouchableOpacity>
        <Text className="text-base text-dark-500 mb-3 mt-8">
          Você está pagando
        </Text>
        <Text className="text-4xl font-semibold text-secondary-900 mb-7">
          R$ {invoiceValue}
        </Text>
        <View className="w-full justify-between flex-row border-b-[1px] pb-2 border-secondary-900 mb-8">
          <Text className="text-secondary-900">Vencimento</Text>
          <Text className="text-secondary-900">
            {`${
              invoiceDueDate
                ? invoiceDueDate + ' ' + invoiceMonth
                : 'Indefinido'
            }`}
          </Text>
        </View>
        {invoiceStatus === 'EM ATRASO' ? (
          <PendingInvoiceAlert />
        ) : (
          <View className="flex flex-1 w-full">
            {!!MarisaAppUtils.copyToClipboard && (
              <MenuOption
                title="Copiar código de barras"
                icon={
                  loadingCopyBarcode ? (
                    <ActivityIndicator />
                  ) : copiedBarcode ? (
                    <CheckSVG />
                  ) : (
                    <CopySVG />
                  )
                }
                action={handleCopyBarcode}
                completed={copiedBarcode}
              />
            )}
            {!!MarisaAppUtils.shareItem && (
              <MenuOption
                title="Compartilhar código de barras"
                icon={
                  loadingShareBarcode ? (
                    <ActivityIndicator />
                  ) : sharedBarcode ? (
                    <CheckSVG />
                  ) : (
                    <ShareSVG />
                  )
                }
                action={handleShareBarcode}
                completed={sharedBarcode}
              />
            )}
          </View>
        )}
      </View>
    </Modal>
  );
};
